/**
 * Audio Manager (SuperAdmin) — every recording + transcript for one schedule day.
 *
 * Picks a date, loads that day's `notes-c` rows from EVERY provider schema in
 * public.schema_settings (each row tagged `_schema`), and lists the patients
 * who have something recorded; the Provider filter narrows to one schema. Each recording plays inline next to
 * its transcript; "Manage" hands the patient to the per-patient
 * AudioManagerModal for rename / delete / backup recovery.
 *
 * Recordings are scoped to the day with filterAudiosForVisitDate — the same
 * rule every other visit view uses — because `more_audios` accumulates across
 * visits on a row and would otherwise show other days' files here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  StatLabel,
  StatNumber,
  StatHelpText,
  Text,
  VStack,
  Alert,
  AlertIcon,
  useToast,
} from "@chakra-ui/react";
import { getSupabase, supabasePublic, supabaseSchema } from "../../components/supabaseClient";
import { tableName, getBackendURL, supabaseUrl } from "../../components/env";
import AudioPlayer from "../../components/table/audio/AudioPlayer";
import { pickAudioDisplayName } from "../../components/table/audio/AudioPlayerModal";
import AudioManagerModal, { clearBackupCache } from "../../components/AudioManagerModal";
import {
  shouldReplace,
  filterAudiosForVisitDate,
  filterTranscriptsForVisitDate,
  transcriptBelongsToAudio,
  transcriptShowsAsLive,
} from "../../utils/audioMetadata";
import { audioMillis, transcriptToText } from "../medstudent/ClinicAudiosModal";
import { mergeSchemaResults } from "../../components/SchedulesTable";
import { isImpersonating } from "../../utils/impersonation";
import { rowsToSchemaGroups } from "../../utils/schemaGroups";
import AudioBucketView, { entryFileKeys, isMovedName } from "./AudioBucketView";
import RepairDialog from "./RepairDialog";
import FindTranscriptDialog from "./FindTranscriptDialog";

// Local calendar day, not UTC — "today" at 9pm Eastern is still today.
const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return "";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, "0")}`;
};

const formatTime = (ms) =>
  ms ? new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";

/**
 * Identity of the stored FILE behind a `more_audios` entry: bucket + object
 * path, so a public URL and a signed URL of the same object are one file.
 */
export function audioFileKey(audio) {
  const raw = String(audio?.url || audio?.audio_url || "").split("?")[0];
  const m = raw.match(/\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)$/);
  if (m) {
    try {
      return `${m[1]}/${decodeURIComponent(m[2])}`;
    } catch {
      return `${m[1]}/${m[2]}`;
    }
  }
  return raw || audio?.session_id || audio?.path || audio?.name || null;
}

/**
 * One entry per stored file. deduplicateBySessionId isn't used here: entries
 * without a session_id fall back to their filename, so two different
 * recordings saved under the same name ("Recording.webm") collapsed into one
 * and the second file vanished from the count. Duplicate entries for the SAME
 * file still merge, keeping the most complete one.
 */
export function dedupeByFile(audios) {
  const byKey = new Map();
  const keyless = [];
  for (const audio of audios || []) {
    const key = audioFileKey(audio);
    if (!key) {
      keyless.push(audio);
      continue;
    }
    const existing = byKey.get(key);
    if (!existing || shouldReplace(existing, audio)) byKey.set(key, audio);
  }
  return [...byKey.values(), ...keyless];
}

const AUDIO_FILE_RE = /\.(webm|mp3|wav|m4a|ogg|mp4)$/i;
const alnum = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * The patient-name part of a stored audio filename, normalised to [a-z0-9].
 * Recorder files are `<patient_name>_Audio<N>_<iso>.webm` (the comma in
 * "Last, First" becomes "_"), mobile ones `<Name>-<EHR>_Audio<N>.webm`,
 * rolling backups `<Name>-<EHR>_Rolling_<slug>.webm`.
 */
export function storageFileOwnerKey(filename) {
  const stem = String(filename || "").replace(AUDIO_FILE_RE, "");
  const m = stem.match(/^(.*?)(?:[_-](?:Audio|Backup|Recording)\d+|_Rolling)(?:[_-].*)?$/i);
  return alnum(m ? m[1] : stem);
}

/**
 * Files sitting in `audio/<schema>/<year>/<date>/` that NO row of that schema
 * links in `more_audios` — an upload whose row update never landed. They are
 * real recordings the chart can't see, so the day scan has to list them.
 *
 * Each is attached to the scheduled patient whose name its filename starts
 * with (longest name wins, so "Li, Ana" doesn't steal "Li, Anabel"'s files);
 * the rest come back `unassigned`.
 *
 * `filesBySchema` = { schema: [{ name, created_at, metadata }] } from a
 * storage list of `bucket`; `urlFor(schema, objectPath, bucket)` builds the
 * playable URL.
 *
 * bucket "audio": the leftovers are DUPLICATE UPLOADS (or the only copy of a
 * recording whose chart update failed). bucket "backup-audio": the recorder's
 * real BACKUPS — `Name-EHR_Rolling_session_….webm` checkpoints written while
 * recording, mobile `Name-EHR_BackupN_….m4a`.
 */
export function assignStorageFiles(rows, filesBySchema, date, urlFor, bucket = "audio") {
  const byRow = new Map();
  const unassigned = [];
  const year = String(date).slice(0, 4);
  const toCandidates = (list) =>
    list
      .map((r) => ({ row: r, key: alnum(r.patient_name) }))
      .filter((c) => c.key)
      .sort((a, b) => b.key.length - a.key.length);
  // Every provider's patients, for files a recorder saved into ANOTHER
  // provider's folder (seen live: a public patient's recordings and rolling
  // backup under STUDENT/…). Same-schema patients are always tried first.
  const allCandidates = toCandidates(rows || []);
  // Keys carry bucket + full path, so one set covers every schema safely.
  // Url AND bucket+path: mobile entries keep their real file in `path`.
  const linked = new Set(
    (rows || []).flatMap((r) =>
      (Array.isArray(r.more_audios) ? r.more_audios : []).flatMap((a) => [audioFileKey(a), ...entryFileKeys(a)]),
    ),
  );
  for (const [schema, files] of Object.entries(filesBySchema || {})) {
    const schemaRows = (rows || []).filter((r) => r._schema === schema);
    const candidates = toCandidates(schemaRows);
    const folder = `${schema}/${year}/${date}`;
    const sizeOf = (f) => Number(f?.metadata?.size) || 0;
    // Linked files in this folder, for spotting an unlinked file that is a
    // second copy of one of them (the recorder popup re-uploading the same
    // blob under the next _AudioN name — same bytes, same size).
    const linkedFiles =
      bucket === "audio" ? (files || []).filter((f) => f?.name && linked.has(`audio/${folder}/${f.name}`)) : [];
    for (const f of files || []) {
      if (!f?.name || !AUDIO_FILE_RE.test(f.name)) continue;
      const path = `${folder}/${f.name}`;
      if (linked.has(`${bucket}/${path}`)) continue;
      const owner = storageFileOwnerKey(f.name);
      const twin =
        sizeOf(f) > 0 &&
        linkedFiles.find((l) => sizeOf(l) === sizeOf(f) && storageFileOwnerKey(l.name) === owner);
      const audio = {
        url: urlFor(schema, path, bucket),
        name: f.name,
        displayName: f.name,
        source: "storage",
        bucket,
        unlinked: true,
        storage_path: path,
        // Storage key of the chart recording this is a copy of, or null when
        // it matches none — then it may be the only copy of that recording.
        duplicateOfKey: twin ? `audio/${folder}/${twin.name}` : null,
        created_at: f.created_at || f.updated_at || null,
        file_size: sizeOf(f) || null,
      };
      const hit =
        owner &&
        (candidates.find((c) => owner.startsWith(c.key)) || allCandidates.find((c) => owner.startsWith(c.key)));
      if (hit) {
        // Filed under a different provider than the patient's schedule row.
        if (hit.row._schema !== schema) audio.storageSchema = schema;
        const k = `${hit.row._schema}::${hit.row.id}`;
        if (!byRow.has(k)) byRow.set(k, []);
        byRow.get(k).push(audio);
      } else {
        unassigned.push({ schema, audio });
      }
    }
  }
  return { byRow, unassigned };
}

const publicAudioUrl = (objectPath, bucket = "audio") =>
  `${String(supabaseUrl || "").replace(/\/$/, "")}/storage/v1/object/public/${bucket}/${objectPath
    .split("/")
    .map(encodeURIComponent)
    .join("/")}`;

/**
 * Which extra uploads of one patient MAY be copies, as { dropKey, keepKey, drop }
 * pairs (keys are "bucket/objectPath"). Size only nominates a pair —
 * removeVerifiedCopies compares the bytes before anything is deleted.
 *
 *  - an extra upload the same size as a chart audio  -> drop it, keep the chart's
 *  - extra uploads the same size as each other       -> keep the oldest, drop the rest
 */
export function planCopyRemovals(summary) {
  const pairs = [];
  const fileKey = (a) => (a?.storage_path ? `${a.bucket || "audio"}/${a.storage_path}` : audioFileKey(a));
  const leftovers = [];
  for (const d of summary?.duplicates || []) {
    if (d.audio.duplicateOfKey) pairs.push({ dropKey: fileKey(d.audio), keepKey: d.audio.duplicateOfKey, drop: d.audio });
    else if (Number(d.audio.file_size) > 0) leftovers.push(d.audio);
  }
  const bySize = new Map();
  for (const a of leftovers) {
    const k = Number(a.file_size);
    if (!bySize.has(k)) bySize.set(k, []);
    bySize.get(k).push(a);
  }
  for (const group of bySize.values()) {
    if (group.length < 2) continue;
    const [keep, ...rest] = [...group].sort((a, b) => audioMillis(a) - audioMillis(b));
    for (const drop of rest) pairs.push({ dropKey: fileKey(drop), keepKey: fileKey(keep), drop });
  }
  return pairs.filter((p) => p.dropKey && p.keepKey && p.dropKey !== p.keepKey);
}

const toHex = (buf) => Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, "0")).join("");
const sha256Hex = async (bytes) => toHex(await crypto.subtle.digest("SHA-256", bytes));

/**
 * Delete each planned copy ONLY when its bytes hash-equal the file it copies.
 * Any download/hash failure or mismatch keeps the file. `readBytes(key)`
 * returns an ArrayBuffer/Uint8Array for "bucket/path"; `deleteFile(pair)` removes it.
 */
export async function removeVerifiedCopies(pairs, { readBytes, deleteFile, hash = sha256Hex }) {
  const digests = new Map();
  const digest = (key) => {
    if (!digests.has(key)) digests.set(key, Promise.resolve(readBytes(key)).then((b) => (b ? hash(b) : null)));
    return digests.get(key);
  };
  const removed = [];
  const kept = [];
  for (const pair of pairs) {
    try {
      const [keepHash, dropHash] = await Promise.all([digest(pair.keepKey), digest(pair.dropKey)]);
      if (keepHash && keepHash === dropHash) {
        await deleteFile(pair);
        removed.push(pair);
      } else {
        kept.push({ ...pair, reason: "contents differ" });
      }
    } catch (err) {
      kept.push({ ...pair, reason: err?.message || "could not verify" });
    }
  }
  return { removed, kept };
}

/** `?date=YYYY-MM-DD` of the page the panel was opened from, if any. */
const urlDate = () => {
  try {
    const d = new URLSearchParams(window.location.search).get("date");
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
  } catch {
    return null;
  }
};

/**
 * One schedule row reduced to what was recorded on `date`.
 *
 * `recordings` are the chart's own audios (more_audios) — the patient's
 * audio count. `duplicates` are unlinked files in the `audio` bucket
 * (`storageAudios`, numbered D1, D2…); `backups` are the recorder's backup
 * files from `backup-audio` (`backupAudios`, numbered B1, B2…). Neither
 * inflates the count or shifts the Audio N numbering.
 */
export function summarizeRowForDay(row, date, storageAudios = [], backupAudios = []) {
  const byTime = (a, b) => audioMillis(a) - audioMillis(b);
  const transcripts = filterTranscriptsForVisitDate(row?.transcripts || [], date, row?.more_audios || []);
  const withTranscripts = (audio, number) => ({
    audio,
    number,
    transcripts: transcripts.filter((t) => transcriptBelongsToAudio(audio, t)),
  });
  const recordings = dedupeByFile(filterAudiosForVisitDate(row?.more_audios || [], date))
    .sort(byTime)
    .map((audio, i) => withTranscripts(audio, i + 1));
  const duplicates = [...storageAudios].sort(byTime).map((audio, i) => {
    const entry = withTranscripts(audio, i + 1);
    // Which chart audio this is a copy of (same size), if any.
    const original = audio.duplicateOfKey
      ? recordings.find((r) => audioFileKey(r.audio) === audio.duplicateOfKey)
      : null;
    return { ...entry, copyOf: original ? original.number : null };
  });
  const backups = [...backupAudios].sort(byTime).map((audio, i) => withTranscripts(audio, i + 1));
  const matched = new Set([...recordings, ...duplicates, ...backups].flatMap((r) => r.transcripts));
  return {
    row,
    recordings,
    duplicates,
    backups,
    transcripts,
    // Transcripts saved for the day that no recording claims (live dictation
    // with no file, or a file that was deleted) — shown so they aren't lost.
    orphanTranscripts: transcripts.filter((t) => !matched.has(t) && !t.is_ai_recap),
    missingTranscripts: recordings.filter((r) => r.transcripts.length === 0).length,
  };
}

function TranscriptText({ transcript }) {
  const text = transcriptToText(transcript);
  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <HStack px={3} py={1.5} bg="gray.50" spacing={2}>
        <Badge colorScheme="blue">{transcriptShowsAsLive(transcript) ? "Live" : "Corti"}</Badge>
        {transcript?.created_at && (
          <Text fontSize="xs" color="gray.500">
            {new Date(transcript.created_at).toLocaleString()}
          </Text>
        )}
        <Box flex="1" />
        {text && (
          <Button size="xs" variant="ghost" onClick={() => navigator.clipboard?.writeText(text).catch(() => {})}>
            Copy
          </Button>
        )}
      </HStack>
      <Box px={3} py={2} maxH="220px" overflowY="auto">
        <Text fontSize="sm" whiteSpace="pre-wrap" color={text ? "gray.700" : "gray.400"} fontStyle={text ? "normal" : "italic"}>
          {text || "This transcript row has no text."}
        </Text>
      </Box>
    </Box>
  );
}

/** One playable recording + its transcripts. `label` overrides the number badge (B1 for backups). */
function RecordingCard({ r, patientId, label = null, tag = null, actions = null }) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={3}>
      <HStack spacing={2} mb={2} wrap="wrap">
        <Badge>{label || r.number}</Badge>
        <Text fontWeight="semibold" fontSize="sm" wordBreak="break-all">{pickAudioDisplayName(r.audio, r.number)}</Text>
        <Text fontSize="xs" color="gray.500">
          {[formatTime(audioMillis(r.audio)), formatDuration(r.audio.duration)].filter(Boolean).join(" · ")}
        </Text>
        {tag}
        {(r.audio.moved_from || isMovedName(r.audio.url || r.audio.name)) && (
          <Badge
            colorScheme="cyan"
            title={
              r.audio.moved_from?.patient_name
                ? `Moved from ${r.audio.moved_from.patient_name} by the Bucket view`
                : "Moved from another patient by the Bucket view"
            }
          >
            moved
          </Badge>
        )}
        {r.audio.storageSchema && (
          <Badge
            colorScheme="purple"
            variant="outline"
            textTransform="none"
            title={`Saved in ${r.audio.storageSchema}'s storage folder, not this patient's provider folder`}
          >
            in {r.audio.storageSchema} folder
          </Badge>
        )}
      </HStack>
      {r.audio.url ? (
        <Flex justify="center" overflowX="auto">
          <AudioPlayer
            audioUrl={r.audio.url}
            audio_duration={r.audio.duration}
            patientId={patientId}
            audioSavedAt={r.audio.uploaded_at || r.audio.recording_date || r.audio.created_at}
            audioDisplayName={pickAudioDisplayName(r.audio, r.number)}
          />
        </Flex>
      ) : (
        <Text fontSize="sm" color="orange.600">No audio file attached — only its transcript was saved.</Text>
      )}
      <VStack align="stretch" spacing={2} mt={3}>
        {r.transcripts.length === 0 ? (
          <Text fontSize="sm" color="gray.500">No transcript saved for this recording.</Text>
        ) : (
          r.transcripts.map((t, i) => <TranscriptText key={t.id || t.session_id || i} transcript={t} />)
        )}
      </VStack>
      {actions && <Box mt={3}>{actions}</Box>}
    </Box>
  );
}

const ALL = "__all__";

export default function AudioDayManagerModal({ isOpen, onClose }) {
  const toast = useToast();
  const [date, setDate] = useState(() => urlDate() || todayLocal());
  const [provider, setProvider] = useState(ALL);
  const [rows, setRows] = useState([]);
  // { schema: [storage list items] } for audio/<schema>/<year>/<date>/
  // { audio: {schema: items}, backup: {schema: items} } — the day's folder in
  // the `audio` and `backup-audio` buckets.
  const [storageFiles, setStorageFiles] = useState({ audio: {}, backup: {} });
  const [storageFailed, setStorageFailed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Provider schemas whose fetch failed (not exposed to PostgREST, no
  // notes-c table, …) — named in a warning rather than silently missing.
  const [failedSchemas, setFailedSchemas] = useState([]);
  const [search, setSearch] = useState("");
  const readOnly = isImpersonating();
  const [repairOpen, setRepairOpen] = useState(false);
  // { row, audio, label } of the untranscribed audio "Find transcript" was clicked on.
  const [findFor, setFindFor] = useState(null);

  // Every provider schema configured in public.schema_settings, read here
  // rather than taken from the page: scanning before the list arrived used to
  // show only the admin's own schema. null = not loaded yet (no scan runs).
  const [providerSchemas, setProviderSchemas] = useState(null);
  // Open on the day the schedule behind it is showing (?date=), not today.
  useEffect(() => {
    if (isOpen) {
      const d = urlDate();
      if (d) setDate(d);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    (async () => {
      let names = [];
      try {
        const { data, error: sErr } = await supabasePublic()
          .from("schema_settings")
          .select("schema_name");
        if (sErr) throw sErr;
        names = rowsToSchemaGroups(data).map((r) => r.schema_name);
      } catch (err) {
        console.warn("[audio-manager] could not read schema_settings:", err?.message);
      }
      if (names.length === 0) names = [supabaseSchema || "public"];
      if (!cancelled) setProviderSchemas([...new Set(names)].sort((a, b) => a.localeCompare(b)));
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const requestRef = useRef(0);
  const load = useCallback(async () => {
    if (!date || !providerSchemas?.length) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const sb = await getSupabase();
      const results = await Promise.all(
        providerSchemas.map(async (schema) => {
          // The day's schedule rows plus the day's folder in both audio
          // buckets: a file can be in storage without any row pointing at it,
          // and the recorder's backups never are on the row.
          const folder = `${schema}/${date.slice(0, 4)}/${date}`;
          const listBucket = (bucket) =>
            sb.storage
              .from(bucket)
              .list(folder, { limit: 1000, sortBy: { column: "name", order: "asc" } })
              .then((res) => (res?.error ? null : (res?.data || []).filter((f) => f.id)))
              .catch(() => null);
          const [{ data, error: qErr }, files, backupFiles] = await Promise.all([
            sb.schema(schema).from(tableName).select("*").eq("date_", date),
            listBucket("audio"),
            listBucket("backup-audio"),
          ]);
          return { schema, rows: qErr ? null : data || [], error: qErr, files, backupFiles };
        }),
      );
      // A slower, older request (date or schema list changed mid-load) must
      // not overwrite the newer one's rows.
      if (requestId !== requestRef.current) return;
      setFailedSchemas(results.filter((r) => r.error).map((r) => r.schema));
      setStorageFailed(
        results.filter((r) => r.files === null || r.backupFiles === null).map((r) => r.schema),
      );
      setStorageFiles({
        audio: Object.fromEntries(results.filter((r) => r.files).map((r) => [r.schema, r.files])),
        backup: Object.fromEntries(results.filter((r) => r.backupFiles).map((r) => [r.schema, r.backupFiles])),
      });
      setRows(
        mergeSchemaResults(results).sort(
          (a, b) =>
            a._schema.localeCompare(b._schema) ||
            String(a.patient_name || "").localeCompare(String(b.patient_name || "")),
        ),
      );
    } catch (err) {
      if (requestId !== requestRef.current) return;
      setRows([]);
      setError(err?.message || "Could not load the schedule for this day.");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [date, providerSchemas]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const urlFor = (_schema, path, bucket) => publicAudioUrl(path, bucket);
  const duplicateAssignment = useMemo(
    () => assignStorageFiles(rows, storageFiles.audio, date, urlFor, "audio"),
    [rows, storageFiles, date],
  );
  const backupAssignment = useMemo(
    () => assignStorageFiles(rows, storageFiles.backup, date, urlFor, "backup-audio"),
    [rows, storageFiles, date],
  );

  const allSummaries = useMemo(
    () =>
      rows.map((r) => {
        const k = `${r._schema}::${r.id}`;
        return summarizeRowForDay(
          r,
          date,
          duplicateAssignment.byRow.get(k) || [],
          backupAssignment.byRow.get(k) || [],
        );
      }),
    [rows, date, duplicateAssignment, backupAssignment],
  );

  const unassignedFiles = useMemo(() => {
    const all = [...duplicateAssignment.unassigned, ...backupAssignment.unassigned];
    return provider === ALL ? all : all.filter((u) => u.schema === provider);
  }, [duplicateAssignment, backupAssignment, provider]);

  // Per-provider counts for the filter dropdown, so it's visible at a glance
  // which providers recorded anything that day.
  const providerCounts = useMemo(() => {
    const counts = {};
    for (const s of allSummaries) {
      if (!counts[s.row._schema]) counts[s.row._schema] = { scheduled: 0, withAudio: 0 };
      const c = counts[s.row._schema];
      c.scheduled += 1;
      if (s.recordings.length) c.withAudio += 1;
    }
    return counts;
  }, [allSummaries]);

  const summaries = useMemo(
    () => (provider === ALL ? allSummaries : allSummaries.filter((s) => s.row._schema === provider)),
    [allSummaries, provider],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    // Every scheduled patient is listed, recorded or not — the per-row
    // counter is what tells them apart.
    if (!q) return summaries;
    return summaries.filter((s) =>
      [s.row.patient_name, s.row.EHR_ID].some((v) => String(v || "").toLowerCase().includes(q)),
    );
  }, [summaries, search]);

  const totals = useMemo(
    () => ({
      scheduled: summaries.length,
      withAudio: summaries.filter((s) => s.recordings.length > 0).length,
      recordings: summaries.reduce((n, s) => n + s.recordings.length, 0),
      missing: summaries.reduce((n, s) => n + s.missingTranscripts, 0),
    }),
    [summaries],
  );

  // Providers the Repair button checks: the filtered one, else all.
  const repairSchemas = useMemo(
    () => (provider === ALL ? providerSchemas || [] : [provider]),
    [provider, providerSchemas],
  );

  // Keyed by schema + id: ids are only unique within one provider's table.
  const rowKey = (row) => `${row._schema}::${row.id}`;

  // ── per-patient actions handed to AudioManagerModal ──────────────────────
  // Same backend calls as PatientPage, but scoped to the patient's own
  // provider schema rather than the admin's.
  const authHeaders = async () => {
    const { data: { session } = {} } = await supabasePublic().auth.getSession();
    return {
      "Content-Type": "application/json",
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    };
  };

  const handleDeleteAudio = async (row, audio) => {
    const patientId = row?.id;
    const schema = row?._schema;
    if (!patientId) return;
    try {
      const resp = await fetch(`${getBackendURL()}/api/transcription-backup/delete-audio/`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
          patient_id: patientId,
          audio_url: audio?.url,
          session_id: audio?.session_id || null,
          schema_name: schema,
          audio_number: audio?.audio_number || null,
        }),
      });
      const result = await resp.json().catch(() => ({}));
      // Storage first: if the file can't be removed, leave the row alone so
      // the DB never points at nothing.
      if (!resp.ok || !result.success) throw new Error(result.error || "Storage deletion failed");

      const sb = await getSupabase();
      const { data: fresh, error: readErr } = await sb
        .schema(schema)
        .from(tableName)
        .select("more_audios, transcripts")
        .eq("id", patientId)
        .single();
      if (readErr) throw readErr;
      const { error: updErr } = await sb
        .schema(schema)
        .from(tableName)
        .update({
          more_audios: (fresh?.more_audios || []).filter((a) => a.url !== audio?.url),
          transcripts: (fresh?.transcripts || []).filter(
            (t) => t.audio_url !== audio?.url && t.audio_file_name !== audio?.name,
          ),
        })
        .eq("id", patientId);
      if (updErr) throw updErr;

      clearBackupCache(patientId);
      toast({ title: "Audio deleted", status: "success", duration: 3000 });
      await load();
    } catch (err) {
      toast({ title: "Delete failed", description: err?.message || String(err), status: "error", duration: 6000, isClosable: true });
    }
  };

  const handleRenameAudio = async (row, audio, newFilenameStem) => {
    const patientId = row?.id;
    const schema = row?._schema;
    try {
      const resp = await fetch(`${getBackendURL()}/api/transcription-backup/rename-audio/`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({
          patient_id: patientId,
          old_audio_url: String(audio?.url || "").trim(),
          new_filename: newFilenameStem,
          schema_name: schema,
        }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.success) throw new Error(result.error || "Rename failed");
      clearBackupCache(patientId);
      toast({ title: "Audio renamed", status: "success", duration: 3000 });
      await load();
    } catch (err) {
      toast({ title: "Rename failed", description: err?.message || String(err), status: "error", duration: 6000, isClosable: true });
      throw err;
    }
  };

  // ── extra uploads (audio-bucket files the chart doesn't list) ────────────
  const [busyFile, setBusyFile] = useState(null);

  /** Put an extra upload on the chart: append it to the row's more_audios. */
  const handleAddToChart = async (row, audio) => {
    if (!window.confirm(`Add "${audio.name}" to ${row.patient_name || "this patient"}'s chart audios?`)) return;
    setBusyFile(audio.url);
    try {
      const sb = await getSupabase();
      const table = sb.schema(row._schema).from(tableName);
      const { data: fresh, error: readErr } = await table.select("more_audios").eq("id", row.id).single();
      if (readErr) throw readErr;
      const current = Array.isArray(fresh?.more_audios) ? fresh.more_audios : [];
      // Re-checked against the live row: someone may have linked it meanwhile.
      if (current.some((a) => audioFileKey(a) === audioFileKey(audio))) {
        toast({ title: "Already on the chart", status: "info", duration: 3000 });
        await load();
        return;
      }
      const when = audio.created_at || new Date().toISOString();
      const entry = {
        name: audio.name,
        displayName: audio.name,
        description: "Linked from storage by the SuperAdmin Audio Manager",
        url: audio.url,
        duration: 0,
        uploaded_at: when,
        recording_date: when,
        recording_timestamp: when,
        source: "superadmin_audio_manager_link",
        type: "audio",
        patient_id: row.id,
        file_size: audio.file_size || null,
      };
      const { error: updErr } = await sb
        .schema(row._schema)
        .from(tableName)
        .update({ more_audios: [...current, entry] })
        .eq("id", row.id);
      if (updErr) throw updErr;
      clearBackupCache(row.id);
      toast({ title: "Added to chart", status: "success", duration: 3000 });
      await load();
    } catch (err) {
      toast({ title: "Add to chart failed", description: err?.message || String(err), status: "error", duration: 6000, isClosable: true });
    } finally {
      setBusyFile(null);
    }
  };

  /** Remove an extra upload's file from storage (it isn't on any chart). */
  const handleDeleteFile = async (row, audio) => {
    if (!window.confirm(`Permanently delete "${audio.name}" from storage? It is not on the chart.`)) return;
    setBusyFile(audio.url);
    try {
      const resp = await fetch(`${getBackendURL()}/api/transcription-backup/delete-file/`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ bucket: audio.bucket || "audio", file_path: audio.storage_path, patient_id: row.id }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.success) throw new Error(result.error || "Delete failed");
      clearBackupCache(row.id);
      toast({ title: "File deleted", status: "success", duration: 3000 });
      await load();
    } catch (err) {
      toast({ title: "Delete failed", description: err?.message || String(err), status: "error", duration: 6000, isClosable: true });
    } finally {
      setBusyFile(null);
    }
  };

  // ── automatic copy removal ───────────────────────────────────────────────
  // After each load, extra uploads that are byte-identical copies (of a chart
  // audio, or of an older extra upload) are deleted from storage. Pairs that
  // were checked and kept are remembered so they aren't re-downloaded on every
  // reload. Never runs while viewing as another user.
  const [removingCopies, setRemovingCopies] = useState(false);
  const checkedPairsRef = useRef(new Set());
  const cleaningRef = useRef(false);
  useEffect(() => {
    if (!isOpen || readOnly || loading || cleaningRef.current) return;
    const pairs = allSummaries
      .flatMap((s) => planCopyRemovals(s).map((p) => ({ ...p, row: s.row })))
      .filter((p) => !checkedPairsRef.current.has(`${p.keepKey}|${p.dropKey}`));
    if (pairs.length === 0) return;
    cleaningRef.current = true;
    setRemovingCopies(true);
    (async () => {
      const sb = await getSupabase();
      const splitKey = (key) => {
        const i = key.indexOf("/");
        return [key.slice(0, i), key.slice(i + 1)];
      };
      const { removed, kept } = await removeVerifiedCopies(pairs, {
        readBytes: async (key) => {
          const [bucket, path] = splitKey(key);
          const { data, error: dlErr } = await sb.storage.from(bucket).download(path);
          if (dlErr || !data) throw new Error(dlErr?.message || "download failed");
          return data.arrayBuffer();
        },
        deleteFile: async (pair) => {
          const [bucket, path] = splitKey(pair.dropKey);
          const resp = await fetch(`${getBackendURL()}/api/transcription-backup/delete-file/`, {
            method: "POST",
            headers: await authHeaders(),
            body: JSON.stringify({ bucket, file_path: path, patient_id: pair.row.id }),
          });
          const result = await resp.json().catch(() => ({}));
          if (!resp.ok || !result.success) throw new Error(result.error || "delete failed");
          clearBackupCache(pair.row.id);
        },
      });
      for (const p of [...removed, ...kept]) checkedPairsRef.current.add(`${p.keepKey}|${p.dropKey}`);
      cleaningRef.current = false;
      setRemovingCopies(false);
      if (removed.length > 0) {
        toast({
          title: `Removed ${removed.length} duplicate cop${removed.length === 1 ? "y" : "ies"}`,
          description: "Byte-identical copies of recordings that are kept. Nothing else was touched.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        await load();
      }
    })();
    // authHeaders/load/toast are stable enough for this one-shot pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, readOnly, loading, allSummaries]);

  /**
   * Folders the inline Audio Manager should scan for this patient's backups
   * besides their own provider's: wherever the day scan found their files.
   */
  const extraFoldersFor = (s) => [
    ...new Set(
      [...s.backups, ...s.duplicates]
        .map((r) => r.audio.storageSchema)
        .filter(Boolean)
        .map((sch) => `${sch}/${date.slice(0, 4)}/${date}`),
    ),
  ];

  // Accordion indexes that are open. The inline Audio Manager only mounts for
  // open patients, so a 40-patient day doesn't run 40 backup scans.
  const [openIndexes, setOpenIndexes] = useState([]);
  useEffect(() => setOpenIndexes([]), [date, provider, search]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Audio Manager</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <Flex gap={3} wrap="wrap" align="flex-end">
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" mb={1}>Provider</Text>
                  <Select size="sm" value={provider} onChange={(e) => setProvider(e.target.value)} minW="240px">
                    <option value={ALL}>
                      {providerSchemas ? `All providers (${providerSchemas.length})` : "Loading providers…"}
                    </option>
                    {(providerSchemas || []).map((s) => {
                      const c = providerCounts[s];
                      return (
                        <option key={s} value={s}>
                          {s}{c ? ` — ${c.withAudio}/${c.scheduled} with audio` : " — none scheduled"}
                        </option>
                      );
                    })}
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" mb={1}>Day</Text>
                  <Input size="sm" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                </Box>
                <Button size="sm" variant="outline" onClick={() => setDate(todayLocal())}>Today</Button>
                <Button size="sm" colorScheme="blue" onClick={load} isLoading={loading}>Refresh</Button>
                {!readOnly && (
                  <Button
                    size="sm"
                    colorScheme="orange"
                    variant="outline"
                    onClick={() => setRepairOpen(true)}
                    isDisabled={!providerSchemas?.length}
                    title="Relink missing audios and transcripts for this day — shows a preview first"
                  >
                    Repair
                  </Button>
                )}
                <Box flex="1" />
                <Input
                  size="sm"
                  maxW="220px"
                  placeholder="Search patient or EHR"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Flex>

              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                <Stat borderWidth="1px" borderRadius="md" p={3}>
                  <StatLabel>Patients scheduled</StatLabel>
                  <StatNumber>{totals.scheduled}</StatNumber>
                  <StatHelpText mb={0}>on {date}</StatHelpText>
                </Stat>
                <Stat borderWidth="1px" borderRadius="md" p={3}>
                  <StatLabel>Patients with audio</StatLabel>
                  <StatNumber>{totals.withAudio}</StatNumber>
                  <StatHelpText mb={0}>at least 1 recording</StatHelpText>
                </Stat>
                <Stat borderWidth="1px" borderRadius="md" p={3}>
                  <StatLabel>Audio files</StatLabel>
                  <StatNumber>{totals.recordings}</StatNumber>
                  <StatHelpText mb={0}>across those patients</StatHelpText>
                </Stat>
                <Stat borderWidth="1px" borderRadius="md" p={3}>
                  <StatLabel>Audio without transcript</StatLabel>
                  <StatNumber color={totals.missing ? "orange.500" : undefined}>{totals.missing}</StatNumber>
                  <StatHelpText mb={0}>needs transcribing</StatHelpText>
                </Stat>
              </SimpleGrid>

              {failedSchemas.length > 0 && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  Couldn’t read {failedSchemas.length} provider schema{failedSchemas.length === 1 ? "" : "s"}:{" "}
                  {failedSchemas.join(", ")}. Their patients aren’t listed.
                </Alert>
              )}
              {removingCopies && (
                <Alert status="info" borderRadius="md">
                  <Spinner size="sm" mr={3} />
                  Checking extra uploads for byte-identical copies and removing them…
                </Alert>
              )}
              {storageFailed.length > 0 && (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  Couldn’t list the audio storage folder for: {storageFailed.join(", ")}. Only files linked on
                  the chart are shown for {storageFailed.length === 1 ? "it" : "them"}.
                </Alert>
              )}
              {error && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {error}
                </Alert>
              )}

              <Tabs variant="enclosed" colorScheme="purple" isLazy>
                <TabList>
                  <Tab>Patients</Tab>
                  <Tab>Bucket view</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
              <VStack align="stretch" spacing={4}>
              {(!providerSchemas || loading) && rows.length === 0 ? (
                <Flex justify="center" py={10}><Spinner /></Flex>
              ) : visible.length === 0 ? (
                <Text color="gray.500" py={6} textAlign="center">
                  {summaries.length === 0
                    ? `No patients scheduled${provider === ALL ? "" : ` for ${provider}`} on ${date}.`
                    : `No scheduled patient matches “${search}”.`}
                </Text>
              ) : (
                <Accordion
                  allowMultiple
                  index={openIndexes}
                  onChange={(i) => setOpenIndexes(Array.isArray(i) ? i : [i])}
                >
                  {visible.map((s, idx) => (
                    <AccordionItem key={rowKey(s.row)}>
                      <Flex align="center">
                        <AccordionButton flex="1">
                          <HStack flex="1" spacing={3} textAlign="left">
                            {/* Audio counter — every patient gets one, 0 included. */}
                            <Flex
                              direction="column"
                              align="center"
                              justify="center"
                              minW="46px"
                              h="40px"
                              borderRadius="md"
                              bg={s.recordings.length ? "purple.500" : "gray.100"}
                              color={s.recordings.length ? "white" : "gray.500"}
                              title={`${s.recordings.length} audio file${s.recordings.length === 1 ? "" : "s"} on ${date}`}
                            >
                              <Text fontWeight="bold" fontSize="md" lineHeight="1">{s.recordings.length}</Text>
                              <Text fontSize="9px" textTransform="uppercase" lineHeight="1.2">audio{s.recordings.length === 1 ? "" : "s"}</Text>
                            </Flex>
                            <Badge colorScheme="teal" variant="outline" textTransform="none">{s.row._schema}</Badge>
                            <Text fontWeight="semibold">{s.row.patient_name || "Unnamed patient"}</Text>
                            {s.row.EHR_ID && <Text fontSize="xs" color="gray.500">{s.row.EHR_ID}</Text>}
                            {s.row.type && <Badge variant="subtle">{s.row.type}</Badge>}
                          </HStack>
                          <HStack spacing={2} mr={2}>
                            {s.backups.length > 0 && (
                              <Badge colorScheme="blue" title="Recorder backups in the backup-audio bucket for this day">
                                {s.backups.length} backup audio{s.backups.length === 1 ? "" : "s"}
                              </Badge>
                            )}
                            {s.duplicates.length > 0 && (
                              <Badge
                                colorScheme="orange"
                                title="Extra files in this day's audio folder that the chart's audio list doesn't include"
                              >
                                {s.duplicates.length} extra upload{s.duplicates.length === 1 ? "" : "s"}
                              </Badge>
                            )}
                            <Badge colorScheme={s.transcripts.length ? "green" : "gray"}>
                              {s.transcripts.length} transcript{s.transcripts.length === 1 ? "" : "s"}
                            </Badge>
                            {s.missingTranscripts > 0 && (
                              <Badge colorScheme="orange">{s.missingTranscripts} untranscribed</Badge>
                            )}
                          </HStack>
                          <AccordionIcon />
                        </AccordionButton>
                      </Flex>
                      <AccordionPanel pb={4}>
                        <VStack align="stretch" spacing={5}>
                          {/* The same Audio Manager the chart pages use — rename,
                              delete, Backups tab with restore (audio + its
                              transcript) and backup delete — pointed at this
                              patient's provider schema plus any other provider
                              folder the day scan found their files in. Mounted
                              only while the row is open. */}
                          {openIndexes.includes(idx) && (
                            <Box borderWidth="1px" borderRadius="md" p={3}>
                              <AudioManagerModal
                                inline
                                isOpen
                                onClose={() => {}}
                                patientData={{ ...s.row, more_audios: s.recordings.map((r) => r.audio) }}
                                patientId={s.row.id}
                                schemaName={s.row._schema}
                                extraBackupFolders={extraFoldersFor(s)}
                                onDeleteAudio={readOnly ? undefined : (audio) => handleDeleteAudio(s.row, audio)}
                                onRenameAudio={
                                  readOnly ? undefined : (audio, stem) => handleRenameAudio(s.row, audio, stem)
                                }
                                onRestoreFromBackupSuccess={load}
                                onFindTranscript={
                                  readOnly ? undefined : (audio, label) => setFindFor({ row: s.row, audio, label })
                                }
                                readOnly={readOnly}
                              />
                            </Box>
                          )}
                          {s.duplicates.length > 0 && (
                            <Box borderLeftWidth="3px" borderColor="orange.300" pl={3}>
                              <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="orange.600" mb={1}>
                                Extra uploads ({s.duplicates.length})
                              </Text>
                              <Text fontSize="xs" color="gray.500" mb={2}>
                                Files in the audio bucket for this day that aren’t on the chart: a copy of a chart audio, or
                                a recording whose chart update never happened. Not counted in the patient’s audios.
                              </Text>
                              <VStack align="stretch" spacing={3}>
                                {s.duplicates.map((r) => (
                                  <RecordingCard
                                    key={r.audio.url}
                                    r={r}
                                    patientId={s.row.id}
                                    label={`D${r.number}`}
                                    actions={
                                      !readOnly && (
                                        <HStack spacing={2} justify="flex-end">
                                          {/* A copy of a chart audio would only duplicate it. */}
                                          {!r.copyOf && (
                                            <Button
                                              size="xs"
                                              colorScheme="green"
                                              variant="outline"
                                              isLoading={busyFile === r.audio.url}
                                              onClick={() => handleAddToChart(s.row, r.audio)}
                                            >
                                              Add to chart
                                            </Button>
                                          )}
                                          <Button
                                            size="xs"
                                            colorScheme="red"
                                            variant="outline"
                                            isLoading={busyFile === r.audio.url}
                                            onClick={() => handleDeleteFile(s.row, r.audio)}
                                          >
                                            Delete file
                                          </Button>
                                        </HStack>
                                      )
                                    }
                                    tag={
                                      r.copyOf ? (
                                        <Badge
                                          colorScheme="orange"
                                          title={`Same size as Audio ${r.copyOf}, which is on the chart — an extra copy saved by the recorder`}
                                        >
                                          copy of Audio {r.copyOf}
                                        </Badge>
                                      ) : (
                                        <Badge
                                          colorScheme="red"
                                          title="Matches no audio on the chart — this may be the only copy of the recording"
                                        >
                                          only copy, not on chart
                                        </Badge>
                                      )
                                    }
                                  />
                                ))}
                              </VStack>
                            </Box>
                          )}
                          {s.orphanTranscripts.length > 0 && (
                            <Box>
                              <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color="gray.500" mb={2}>
                                Transcripts without a recording
                              </Text>
                              <VStack align="stretch" spacing={2}>
                                {s.orphanTranscripts.map((t, i) => <TranscriptText key={t.id || t.session_id || i} transcript={t} />)}
                              </VStack>
                            </Box>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}

              {unassignedFiles.length > 0 && (
                <Box borderWidth="1px" borderColor="orange.200" borderRadius="md" p={3}>
                  <Text fontWeight="semibold" mb={1}>
                    {unassignedFiles.length} audio file{unassignedFiles.length === 1 ? "" : "s"} in storage not matched to a scheduled patient
                  </Text>
                  <Text fontSize="xs" color="gray.500" mb={3}>
                    In the {date} audio or backup-audio folder, not linked on any chart, and the filename doesn’t
                    start with a scheduled patient’s name.
                  </Text>
                  <VStack align="stretch" spacing={3}>
                    {unassignedFiles.map(({ schema: s, audio }) => (
                      <Box key={audio.url} borderWidth="1px" borderRadius="md" p={2}>
                        <HStack spacing={2} mb={1}>
                          <Badge colorScheme="teal" variant="outline" textTransform="none">{s}</Badge>
                          <Badge colorScheme={audio.bucket === "backup-audio" ? "blue" : "orange"}>
                            {audio.bucket === "backup-audio" ? "backup" : "audio"}
                          </Badge>
                          <Text fontSize="sm" fontWeight="semibold" wordBreak="break-all">{audio.name}</Text>
                        </HStack>
                        <Flex justify="center" overflowX="auto">
                          <AudioPlayer audioUrl={audio.url} audioSavedAt={audio.created_at} audioDisplayName={audio.name} />
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
              </VStack>
                  </TabPanel>
                  <TabPanel px={0}>
                    {/* Storage explorer over the audio / backup-audio /
                        transcription-backups buckets; moves any file (with
                        its transcripts) onto one of this day's patients. */}
                    <AudioBucketView
                      summaries={visible}
                      provider={provider}
                      allRows={rows}
                      date={date}
                      providerSchemas={providerSchemas}
                      readOnly={readOnly}
                      onChanged={load}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          </ModalBody>
          {/* Dialogs opened from this panel must render INSIDE its ModalContent:
              Chakra scroll-locks only the first open modal (this one), and that
              lock cancels mouse-wheel events from anything outside its React
              tree — a sibling dialog's scrollbar then ignores the wheel. */}
          {repairOpen && (
            <RepairDialog
              isOpen
              onClose={() => setRepairOpen(false)}
              date={date}
              schemas={repairSchemas}
              dayRows={rows}
              onDone={load}
            />
          )}
          {findFor && (
            <FindTranscriptDialog
              isOpen
              onClose={() => setFindFor(null)}
              row={findFor.row}
              audio={findFor.audio}
              label={findFor.label}
              schemas={providerSchemas || []}
              onDone={load}
            />
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
